//src/pages/orders/OrdersListPage/OrdersListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import OrdersAPI from '../../../api/v3/orders-api-v3';
import { useAuth } from '../../../providers/auth-provider';

const BASE = '/app/sales/bagspa';
const DEFAULT_VENDOR = 'kaya';
const DEFAULT_SORT = { key: 'received_at', dir: 'desc' };
const HYDRATE_CONCURRENCY = 4;
const DERIVED_QC_STATUSES = ['back_to_bnk_qc', 'ready_for_qc'];
const normVendor = (v) => (v || 'kaya').toString().trim().toLowerCase();

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'paid_dp', label: 'Paid DP' },
  { value: 'paid_full', label: 'Paid Full' },
  { value: 'vendor_processing', label: 'Send to Vendor' },
  { value: 'back_to_bnk_qc', label: 'Back to BNK / QC' },
  { value: 'ready_for_qc', label: 'Ready for QC' },
  { value: 'need_packing_customer', label: 'Need Packing Customer' },
  { value: 'waiting_customer_pickup', label: 'Waiting Customer Pickup' },
  { value: 'complete', label: 'Complete (Delivered)' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'returned', label: 'Returned' },
  { value: 'refunded', label: 'Refunded' },
];

const EXPORT_HEADERS = [
  'Vendor',
  'Order No',
  'Order Item ID',
  'Customer Name',
  'Status Order',
  'Receive Date',
  'Completion Date',
  'Brand',
  'Category',
  'SKU',
  'Item Specification',
  'Order Type',
  'Treatment',
  'Notes',
  'Delivery Cost',
  'Initial Price (Admin)',
  'Initial Price customer',
  'Final Price (from Kaya)',
  'Final Price customer',
  'Address',
  'Phone Number',
  'Sales Assistant',
  'Assist By',
  'Voucher Code',
  'Voucher Discount',
  'Voucher Redeemed At',
];

const STATUS_COLOR_MAP = {
  draft: 'secondary',
  confirmed: 'info',
  processing: 'primary',
  completed: 'success',
  cancelled: 'dark',
  accepted: 'warning',
  paid_dp: 'warning',
  paid_full: 'success',
  vendor_processing: 'primary',
  back_to_bnk_qc: 'info',
  ready_for_qc: 'info',
  customer_pick_up_process: 'info',
  need_packing_customer: 'warning',
  waiting_customer_pickup: 'warning',
  complete: 'success',
  returned: 'dark',
  refunded: 'dark',
};

const STATUS_LABEL_MAP = {
  draft: 'Pending',
  vendor_processing: 'Send to Vendor',
  back_to_bnk_qc: 'Back to BNK / QC',
  ready_for_qc: 'Ready for QC',
  customer_pick_up_process: 'Back to BNK / QC',
  need_packing_customer: 'Need Packing Customer',
  waiting_customer_pickup: 'Waiting Customer Pickup',
};

const STATUS_NORMALIZER = {
  '0': 'draft',
  '1': 'confirmed',
  '2': 'processing',
  '3': 'completed',
  '4': 'cancelled',
  '5': 'accepted',
  '6': 'paid_dp',
  '7': 'paid_full',
  '8': 'vendor_processing',
  '9': 'customer_pick_up_process',
  '10': 'complete',
  '11': 'returned',
  '12': 'refunded',
  '13': 'need_packing_customer',
  '14': 'waiting_customer_pickup',
  draft: 'draft',
  confirmed: 'confirmed',
  processing: 'processing',
  completed: 'completed',
  cancelled: 'cancelled',
  accepted: 'accepted',
  paid_dp: 'paid_dp',
  paid_full: 'paid_full',
  vendor_processing: 'vendor_processing',
  customer_pick_up_process: 'customer_pick_up_process',
  complete: 'complete',
  returned: 'returned',
  refunded: 'refunded',
  need_packing_customer: 'need_packing_customer',
  waiting_customer_pickup: 'waiting_customer_pickup',
};

// ---------- generic helpers ----------
function fmtIDR(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `Rp ${Number(value).toLocaleString('id-ID')}`;
}

function fmtDate(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY');
}

function fmtDateTime(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY HH:mm');
}

function toDateMs(value) {
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
}

function toNum(value) {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
}

function normalizeStatus(status) {
  if (status == null) return '';
  return STATUS_NORMALIZER[String(status).toLowerCase()] || String(status).toLowerCase();
}

function deriveOrderStatusView(rowOrStatus) {
  if (typeof rowOrStatus === 'object' && rowOrStatus !== null) {
    const normalized = normalizeStatus(rowOrStatus?.status);
    const vendor = normVendor(rowOrStatus?.vendor);

    if (normalized === 'customer_pick_up_process') {
      return vendor === 'bnk' ? 'ready_for_qc' : 'back_to_bnk_qc';
    }

    return normalized;
  }

  return normalizeStatus(rowOrStatus);
}

function formatStatusLabel(rowOrStatus) {
  const normalized = deriveOrderStatusView(rowOrStatus);
  if (!normalized) return 'Unknown';
  return STATUS_LABEL_MAP[normalized] || `${normalized[0].toUpperCase()}${normalized.slice(1).replace(/_/g, ' ')}`;
}

function statusBadge(rowOrStatus) {
  const normalized = deriveOrderStatusView(rowOrStatus);
  const color = STATUS_COLOR_MAP[normalized] || 'light';
  const label = formatStatusLabel(rowOrStatus);
  return <span className={`badge badge-${color}`}>{label}</span>;
}

// ---------- vendor helpers ----------
function normalizeVendor(vendor) {
  return String(vendor || DEFAULT_VENDOR).toLowerCase();
}

function vendorLabel(vendor) {
  const normalized = normalizeVendor(vendor);
  if (normalized === 'bnk') return 'BNK';
  if (normalized === 'kaya') return 'Kaya';
  return '—';
}

function vendorBadge(vendor) {
  const normalized = normalizeVendor(vendor);
  const color = normalized === 'bnk' ? 'dark' : normalized === 'kaya' ? 'success' : 'secondary';
  return <span className={`badge badge-${color}`}>{vendorLabel(normalized)}</span>;
}

// ---------- xlsx loader ----------
let xlsxLoaderPromise = null;

async function ensureXLSX() {
  if (window.XLSX) return window.XLSX;

  if (!xlsxLoaderPromise) {
    xlsxLoaderPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-sheetjs-loader="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.XLSX));
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
      script.async = true;
      script.dataset.sheetjsLoader = 'true';
      script.onload = () => resolve(window.XLSX);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return xlsxLoaderPromise;
}

// ---------- order / item helpers ----------
function orderTypeLabel(value) {
  return String(value || 'regular').toLowerCase() === 'priority' ? 'Priority' : 'Regular';
}

function compileAddress(customer) {
  const addresses = Array.isArray(customer?.addresses) ? customer.addresses : [];
  if (!addresses.length) return '';

  const primary =
    addresses.find((item) => item.is_customer_default) ||
    addresses.find((item) => String(item.alias || '').toLowerCase().includes('utama')) ||
    addresses[0];

  return [
    primary?.street,
    primary?.subdistrict,
    primary?.district,
    primary?.city,
    primary?.province,
    primary?.postal_code,
  ]
    .filter(Boolean)
    .join(', ');
}

function getSalesAssistantName(orderDetail, orderSummary) {
  return (
    orderDetail?.sales_assistant?.first_name ||
    orderDetail?.sales_assistant_name ||
    orderDetail?.sales_assistant_email ||
    orderSummary?.sales_assistant?.first_name ||
    orderSummary?.sales_assistant_name ||
    orderSummary?.sales_assistant_email ||
    '—'
  );
}

function getAssistByName(orderDetail, orderSummary) {
  return (
    orderDetail?.assist_by?.label ||
    orderDetail?.assist_by?.first_name ||
    orderDetail?.assist_by?.email ||
    orderSummary?.assist_by?.label ||
    orderSummary?.assist_by?.first_name ||
    orderSummary?.assist_by?.email ||
    '—'
  );
}

function numberOrZero(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function summarizeSkus(order) {
  if (Array.isArray(order?.skus)) return order.skus.filter(Boolean).join(', ');
  return String(order?.skus || '').trim();
}

function mapOrderFallbackRow(order) {
  const skuSummary = summarizeSkus(order);

  return {
    order_id: order.id,
    vendor: order?.vendor ?? DEFAULT_VENDOR,
    order_no: order.order_no || `#${order.id}`,
    status: order.status,
    received_at: order.created_at,
    completion_date: order.estimated_completion_date,
    customer: order.customer || {},
    delivery_cost: order.delivery_cost || 0,
    sales_assistant:
      order?.sales_assistant?.first_name ||
      order?.sales_assistant_name ||
      order?.sales_assistant_email ||
      '—',
    assist_by:
      order?.assist_by?.label ||
      order?.assist_by?.first_name ||
      order?.assist_by?.email ||
      '—',
    voucher: order?.voucher || null,
    address: '',
    qc: order?.qc || {},
    item_id: null,
    sku: skuSummary,
    brand_name: '',
    category_name: '',
    spec: skuSummary,
    order_type: 'regular',
    treatment: order?.order_category_name || '',
    notes: '',
    initial_vendor_price: numberOrZero(order?.initial_vendor_price),
    initial_fixed_price: numberOrZero(order?.initial_fixed_price),
    final_vendor_price: numberOrZero(order?.final_vendor_price),
    final_fixed_price: numberOrZero(order?.final_fixed_price),
  };
}

function mapOrderFallbackRowsFromSummary(order) {
  const itemSummaries = Array.isArray(order?.item_summaries) ? order.item_summaries : [];

  if (!itemSummaries.length) {
    return [mapOrderFallbackRow(order)];
  }

  const skuSummary = summarizeSkus(order);

  return itemSummaries.map((item) => {
    const brandName = String(item?.brand_name || '').trim();
    const categoryName = String(item?.category_name || '').trim();
    const sku = String(item?.sku || '').trim();
    const spec = [brandName, categoryName, sku].filter(Boolean).join(' ');

    return {
      order_id: order.id,
      vendor: order?.vendor ?? DEFAULT_VENDOR,
      order_no: order.order_no || `#${order.id}`,
      status: order.status,
      received_at: order.created_at,
      completion_date: order.estimated_completion_date,
      customer: order.customer || {},
      delivery_cost: order.delivery_cost || 0,
      sales_assistant:
        order?.sales_assistant?.first_name ||
        order?.sales_assistant_name ||
        order?.sales_assistant_email ||
        '—',
      assist_by:
        order?.assist_by?.label ||
        order?.assist_by?.first_name ||
        order?.assist_by?.email ||
        '—',
      voucher: order?.voucher || null,
      address: '',
      qc: order?.qc || {},
      item_id: item?.id ?? null,
      sku: sku || skuSummary,
      brand_name: brandName,
      category_name: categoryName,
      spec: spec || sku || skuSummary,
      order_type: item?.order_type || 'regular',
      treatment: item?.order_category_name || order?.order_category_name || '',
      notes: '',
      initial_vendor_price: numberOrZero(item?.initial_vendor_price),
      initial_fixed_price: numberOrZero(item?.initial_fixed_price),
      final_vendor_price: numberOrZero(item?.final_vendor_price),
      final_fixed_price: numberOrZero(item?.final_fixed_price),
    };
  });
}

function buildBaseRow(orderSummary, orderDetail, customer, voucher, address) {
  return {
    order_id: orderSummary.id,
    vendor: orderDetail?.vendor ?? orderSummary?.vendor ?? DEFAULT_VENDOR,
    order_no: orderSummary.order_no || `#${orderSummary.id}`,
    status: orderSummary.status,
    received_at: orderSummary.created_at,
    completion_date: orderSummary.estimated_completion_date,
    customer: orderSummary.customer || {},
    delivery_cost: orderSummary.delivery_cost || 0,
    sales_assistant: getSalesAssistantName(orderDetail, orderSummary),
    assist_by: getAssistByName(orderDetail, orderSummary),
    voucher,
    address,
    qc: orderDetail?.qc || orderSummary?.qc || {},
    notes: String(orderDetail?.additional_notes || '').trim(),
  };
}

function mapZeroItemRow(orderSummary, orderDetail, customer) {
  const voucher = orderDetail?.voucher || orderSummary?.voucher || null;
  const address = compileAddress(customer);
  const base = buildBaseRow(orderSummary, orderDetail, customer, voucher, address);

  return {
    ...base,
    item_id: null,
    sku: '',
    brand_name: '',
    category_name: '',
    spec: '',
    order_type: 'regular',
    treatment: orderDetail?.order_category_name || orderSummary?.order_category_name || '',
    initial_vendor_price: numberOrZero(orderDetail?.initial_vendor_price ?? orderSummary?.initial_vendor_price),
    initial_fixed_price: numberOrZero(orderDetail?.initial_fixed_price ?? orderSummary?.initial_fixed_price),
    final_vendor_price: numberOrZero(orderDetail?.final_vendor_price ?? orderSummary?.final_vendor_price),
    final_fixed_price: numberOrZero(orderDetail?.final_fixed_price ?? orderSummary?.final_fixed_price),
  };
}

function mapItemRows(orderSummary, orderDetail, customer) {
  const voucher = orderDetail?.voucher || orderSummary?.voucher || null;
  const address = compileAddress(customer);
  const base = buildBaseRow(orderSummary, orderDetail, customer, voucher, address);
  const orderItems = Array.isArray(orderDetail?.order_items) ? orderDetail.order_items : [];

  if (!orderItems.length) {
    return [mapZeroItemRow(orderSummary, orderDetail, customer)];
  }

  return orderItems.map((item) => {
    const spec = [item.brand_name, item.category_name, item.sku, String(item.extra_info || '').trim()]
      .filter(Boolean)
      .join(' ');

    return {
      ...base,
      item_id: item.id,
      sku: item.sku || '',
      brand_name: item.brand_name || '',
      category_name: item.category_name || '',
      spec,
      order_type: item.order_type || 'regular',
      treatment:
        item.order_category_name ||
        item.layanan_name ||
        orderDetail?.order_category_name ||
        orderSummary?.order_category_name ||
        '',
      initial_vendor_price: numberOrZero(item.initial_vendor_price),
      final_vendor_price: numberOrZero(item.final_vendor_price),
      initial_fixed_price: numberOrZero(item.initial_fixed_price ?? item.initial_price),
      final_fixed_price: numberOrZero(item.final_fixed_price ?? item.final_price),
    };
  });
}

async function hydrateOrdersToItems(orderList) {
  const rows = [];
  let cursor = 0;

  async function worker() {
    while (cursor < orderList.length) {
      const currentIndex = cursor++;
      const orderSummary = orderList[currentIndex];

      try {
        const detailResponse = await OrdersAPI.show(orderSummary.id);

        const orderDetail = detailResponse?.item || detailResponse?.data || detailResponse || {};
        const customer = orderDetail?.customer || orderSummary?.customer || {};
        rows.push(...mapItemRows(orderSummary, orderDetail, customer));
      } catch (_) {
        rows.push(mapOrderFallbackRow(orderSummary));
      }
    }
  }

  await Promise.all(Array.from({ length: HYDRATE_CONCURRENCY }, () => worker()));

  return rows.sort((a, b) => toDateMs(b.received_at) - toDateMs(a.received_at));
}

function extractOrderList(response) {
  return response?.items ?? response?.data ?? (Array.isArray(response) ? response : []);
}

function extractOrderMeta(response) {
  return response?.meta || {};
}

function mapOrderSummariesToExportRows(orderList) {
  return orderList.flatMap(mapOrderFallbackRowsFromSummary);
}

async function fetchAllOrdersForExport() {
  const allOrders = [];
  const seenIds = new Set();
  const maxPages = 500;

  function pushUnique(order, fallbackKey) {
    const key = order?.id ?? `${order?.order_no || 'order'}-${fallbackKey}`;
    if (seenIds.has(key)) return;
    seenIds.add(key);
    allOrders.push(order);
  }

  const firstResponse = await OrdersAPI.list({ page: 1, per: 100 });
  const firstList = extractOrderList(firstResponse);
  const firstMeta = extractOrderMeta(firstResponse);

  firstList.forEach((order, index) => pushUnique(order, `1-${index}`));

  const parsedTotalPages = Number(firstMeta.total_pages);
  const totalPages = Number.isFinite(parsedTotalPages) && parsedTotalPages > 0
    ? Math.min(parsedTotalPages, maxPages)
    : 1;

  for (let apiPage = 2; apiPage <= totalPages; apiPage += 1) {
    const response = await OrdersAPI.list({ page: apiPage, per: 100 });
    const list = extractOrderList(response);

    if (!list.length) break;
    list.forEach((order, index) => pushUnique(order, `${apiPage}-${index}`));
  }

  return allOrders;
}

// ---------- QC helpers ----------
function qcAdminId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function qcAdminLabel(admin) {
  if (!admin) return '-';
  return admin.label || admin.first_name || admin.email || `Admin #${admin.id}`;
}

function shouldShowQcColumns(row) {
  const status = normalizeStatus(row?.status);
  return (
    status === 'customer_pick_up_process' ||
    status === 'complete' ||
    !!row?.qc?.slot_one_done ||
    !!row?.qc?.slot_two_done
  );
}

function canCheckQcSlot(row, slot, isSaving, currentAdminId) {
  const qc = row?.qc || {};
  const currentId = qcAdminId(currentAdminId);

  if (isSaving || !currentId) return false;
  if (slot === 1) return !!qc.slot_one_available;
  if (slot === 2) return !!qc.slot_two_available;
  return false;
}

function qcPendingHint(row, slot, currentAdminId) {
  const qc = row?.qc || {};
  const currentId = qcAdminId(currentAdminId);
  const slotOneAdminId = qcAdminId(qc?.slot_one_admin?.id ?? qc?.slot_one_admin_id);

  if (slot === 2 && !qc.slot_one_done) return 'Wait QC1';
  if (slot === 2 && currentId != null && slotOneAdminId === currentId) return 'Wait QC2';
  return '-';
}

function adminHasCheckedQc(row, currentAdminId) {
  const qc = row?.qc || {};
  const currentId = qcAdminId(currentAdminId);
  if (!currentId) return false;

  const slotOneAdminId = qcAdminId(qc?.slot_one_admin?.id ?? qc?.slot_one_admin_id);
  const slotTwoAdminId = qcAdminId(qc?.slot_two_admin?.id ?? qc?.slot_two_admin_id);

  return (qc.slot_one_done && slotOneAdminId === currentId) ||
    (qc.slot_two_done && slotTwoAdminId === currentId);
}

function canDeclineQc(row, isSaving, currentAdminId) {
  return !!row?.qc?.decline_available && !isSaving && !adminHasCheckedQc(row, currentAdminId);
}

// ---------- export helpers ----------
function buildExportRows(rows) {
  return rows.map((row) =>
    EXPORT_HEADERS.map((header) => {
      switch (header) {
        case 'Vendor':
          return vendorLabel(row.vendor);
        case 'Order No':
          return row.order_no;
        case 'Order Item ID':
          return row.item_id || '';
        case 'Customer Name':
          return row.customer?.name || '';
        case 'Status Order':
          return normalizeStatus(row.status);
        case 'Receive Date':
          return row.received_at ? dayjs(row.received_at).format('YYYY-MM-DD') : '';
        case 'Completion Date':
          return row.completion_date ? dayjs(row.completion_date).format('YYYY-MM-DD') : '';
        case 'Brand':
          return row.brand_name || '';
        case 'Category':
          return row.category_name || '';
        case 'SKU':
          return row.sku || '';
        case 'Item Specification':
          return row.spec || '';
        case 'Order Type':
          return orderTypeLabel(row.order_type);
        case 'Treatment':
          return row.treatment || '';
        case 'Notes':
          return row.notes || '';
        case 'Delivery Cost':
          return Number(row.delivery_cost || 0);
        case 'Initial Price (Admin)':
          return Number(row.initial_vendor_price || 0);
        case 'Initial Price customer':
          return Number(row.initial_fixed_price || 0);
        case 'Final Price (from Kaya)':
          return Number(row.final_vendor_price || 0);
        case 'Final Price customer':
          return Number(row.final_fixed_price || 0);
        case 'Address':
          return row.address || '';
        case 'Phone Number':
          return row.customer?.phone || '';
        case 'Sales Assistant':
          return row.sales_assistant || '';
        case 'Assist By':
          return row.assist_by || '';
        case 'Voucher Code':
          return row.voucher?.code || '';
        case 'Voucher Discount':
          return Number(row.voucher?.discount_amount || 0);
        case 'Voucher Redeemed At':
          return row.voucher?.redeemed_at ? dayjs(row.voucher.redeemed_at).format('YYYY-MM-DD HH:mm') : '';
        default:
          return '';
      }
    })
  );
}

function escapeCsvCell(value) {
  if (value == null) return '';
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function filterRows(rows, { statusFilter, hasVoucherOnly, receivedFrom, receivedTo }) {
  let list = [...rows];

  if (statusFilter) {
    list = list.filter((row) => deriveOrderStatusView(row) === statusFilter);
  }

  if (hasVoucherOnly) {
    list = list.filter((row) => !!row.voucher);
  }

  if (receivedFrom) {
    const fromMs = Date.parse(`${receivedFrom}T00:00:00`);
    list = list.filter((row) => toDateMs(row.received_at) >= fromMs);
  }

  if (receivedTo) {
    const toMs = Date.parse(`${receivedTo}T23:59:59.999`);
    list = list.filter((row) => toDateMs(row.received_at) <= toMs);
  }

  return list;
}

function sortRows(rows, sortBy) {
  const list = [...rows];
  const { key, dir } = sortBy;

  function compare(a, b) {
    if (a < b) return dir === 'asc' ? -1 : 1;
    if (a > b) return dir === 'asc' ? 1 : -1;
    return 0;
  }

  list.sort((a, b) => {
    switch (key) {
      case 'order_id':
        return compare(toNum(a.order_id), toNum(b.order_id));
      case 'order_no':
        return compare(String(a.order_no || '').toLowerCase(), String(b.order_no || '').toLowerCase());
      case 'customer':
        return compare(String(a.customer?.name || '').toLowerCase(), String(b.customer?.name || '').toLowerCase());
      case 'brand':
        return compare(String(a.brand_name || '').toLowerCase(), String(b.brand_name || '').toLowerCase());
      case 'category':
        return compare(String(a.category_name || '').toLowerCase(), String(b.category_name || '').toLowerCase());
      case 'voucher':
        return compare(String(a.voucher?.code || '').toLowerCase(), String(b.voucher?.code || '').toLowerCase());
      case 'received_at':
        return compare(toDateMs(a.received_at), toDateMs(b.received_at));
      case 'completion_date':
        return compare(toDateMs(a.completion_date), toDateMs(b.completion_date));
      case 'final_customer':
        return compare(toNum(a.final_fixed_price), toNum(b.final_fixed_price));
      default:
        return 0;
    }
  });

  return list;
}

function SortableTh({ sortBy, sortKey, onToggle, className = '', style, children }) {
  const indicator = sortBy.key === sortKey ? (sortBy.dir === 'asc' ? '▲' : '▼') : '';
  return (
    <th role="button" onClick={() => onToggle(sortKey)} className={className} style={style}>
      {children} {indicator}
    </th>
  );
}

function VoucherCell({ voucher }) {
  if (!voucher) return <span className="text-muted">—</span>;

  return (
    <>
      <span className="badge badge-info">{voucher.code}</span>
      <div className="small text-muted">- {fmtIDR(voucher.discount_amount)}</div>
      {voucher.redeemed_at && (
        <div className="small text-muted">{dayjs(voucher.redeemed_at).format('DD MMM YYYY HH:mm')}</div>
      )}
    </>
  );
}

function QcCell({ row, slot, isSaving, currentAdminId, onCheck }) {
  const qc = row?.qc || {};
  const done = slot === 1 ? qc.slot_one_done : qc.slot_two_done;
  const admin = slot === 1 ? qc.slot_one_admin : qc.slot_two_admin;
  const doneAt = slot === 1 ? qc.slot_one_at : qc.slot_two_at;

  if (!shouldShowQcColumns(row)) {
    return <span className="text-muted">-</span>;
  }

  if (done) {
    return (
      <div>
        <span className="badge badge-success">Done</span>
        <div className="text-muted mt-1">{qcAdminLabel(admin)}</div>
        <div className="text-muted">{fmtDateTime(doneAt)}</div>
      </div>
    );
  }

  if (canCheckQcSlot(row, slot, isSaving, currentAdminId)) {
    return (
      <input
        type="checkbox"
        checked={false}
        disabled={isSaving}
        onChange={() => onCheck(row.order_id, slot)}
        title={`QC ${slot}`}
      />
    );
  }

  return <span className="text-muted">{qcPendingHint(row, slot, currentAdminId)}</span>;
}

export default function OrdersListPage() {
  const { user } = useAuth();
  const currentAdminId = user?.id;

  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);
  const [meta, setMeta] = useState({ page: 1, per: 25, total: 0, total_pages: 1 });
  const [hydrating, setHydrating] = useState(false);
  const [exportingMode, setExportingMode] = useState('');
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [hasVoucherOnly, setHasVoucherOnly] = useState(false);

  const [vendorKaya, setVendorKaya] = useState(true);
  const [vendorBNK, setVendorBNK] = useState(true);

  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedTo, setReceivedTo] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [qcSaving, setQcSaving] = useState({});

  const [qcModal, setQcModal] = useState({
    open: false,
    slot: null,
    title: '',
    message: '',
    helper: '',
  });

  function buildListParams() {
  const params = {
    q: search,
    page,
    per,
  };

  if (statusFilter && !DERIVED_QC_STATUSES.includes(statusFilter)) params.status = statusFilter;
  if (voucherCode) params.voucher_code = voucherCode;
  if (hasVoucherOnly) params.has_voucher_only = true;
  if (receivedFrom) params.received_from = receivedFrom;
  if (receivedTo) params.received_to = receivedTo;

  try {
    params.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_) {
    // noop
  }

  const selectedVendors = [];
  if (vendorKaya) selectedVendors.push('kaya');
  if (vendorBNK) selectedVendors.push('bnk');
  if (selectedVendors.length) params.vendor = selectedVendors;

  return params;
}

  async function load() {
    try {
      setLoading(true);
      setHydrating(false);
      setError('');

      const response = await OrdersAPI.list(buildListParams());
      const list = extractOrderList(response);
      const responseMeta = extractOrderMeta(response);

      setOrders(list);
      setMeta({
        page: Number(responseMeta.page || page),
        per: Number(responseMeta.per || per),
        total: Number(responseMeta.total || list.length),
        total_pages: Number(responseMeta.total_pages || 1),
      });

      // regression guard
      const summaryRows = list.flatMap(mapOrderFallbackRowsFromSummary);
      setItems(summaryRows);
      setLoading(false);

      // Keep existing list behavior safe:
      // render summary first, then enrich the current page only.
      // Export All does not use this hydrate flow, so it stays fast.
      if (list.length) {
        setHydrating(true);
        const detailedRows = await hydrateOrdersToItems(list);
        setItems(detailedRows);
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setHydrating(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    
  }, [page, per, statusFilter]);

  function applyFilters() {
    if (page !== 1) {
      setPage(1);
      return;
    }
    load();
  }

  function extractQcResponseItem(response) {
  return (
    response?.item ||
    response?.data?.item ||
    response?.order ||
    response?.data?.order ||
    {}
  );
}

function extractQcResponseMessage(response) {
  return response?.message || response?.data?.message || '';
}

function isQcCompletedAfterQc2(slot, response) {
  const item = extractQcResponseItem(response);
  const message = extractQcResponseMessage(response).toLowerCase();

  const orderStatus = normalizeStatus(item?.status);
  const shipmentStatus = normalizeStatus(item?.current_shipment_status);

  return (
    Number(slot) === 2 &&
    (
      orderStatus === 'need_packing_customer' ||
      shipmentStatus === 'need_packing_customer' ||
      message.includes('qc completed')
    )
  );
}

function buildQcModalCopy({ slot, response }) {
  if (isQcCompletedAfterQc2(slot, response)) {
    return {
      slot,
      title: 'QC Completed',
      message:
        'QC completed. Status order saat ini adalah "Need Packing to Customer".',
      helper:
        'Order sudah keluar dari QC list dan akan dilanjutkan proses packing customer oleh tim Warehouse.',
    };
  }

  if (Number(slot) === 1) {
    return {
      slot,
      title: 'QC 1 Saved',
      message: 'QC 1 completed. Order masih menunggu QC 2 dari admin berbeda.',
      helper:
        'Setelah QC 2 selesai, order akan masuk ke status Need Packing to Customer.',
    };
  }

  return {
    slot,
    title: `QC ${slot} Saved`,
    message: extractQcResponseMessage(response) || `QC ${slot} berhasil disimpan.`,
    helper:
      'Data QC berhasil diproses. Halaman akan menampilkan status terbaru setelah refresh data selesai.',
  };
}

  function openQcModal({ slot, title, message, helper }) {
    const slotLabel = slot ? `QC ${slot}` : 'QC';

    setQcModal({
      open: true,
      slot,
      title: title || `${slotLabel} Saved`,
      message: message || `${slotLabel} berhasil disimpan.`,
      helper:
        helper ||
        'Data QC berhasil diproses. Halaman akan menampilkan status terbaru setelah refresh data selesai.',
    });
  }

  function closeQcModal() {
    setQcModal({
      open: false,
      slot: null,
      title: '',
      message: '',
      helper: '',
    });
  }

  async function handleQcCheck(orderId, slot) {
    const key = String(orderId);

    try {
      setQcSaving((prev) => ({ ...prev, [key]: true }));
      const response = await OrdersAPI.qcCheck(orderId, { slot });

      openQcModal(buildQcModalCopy({ slot, response }));

      await load();
    } catch (e) {
      window.alert(e.message || 'Failed to submit QC.');
    } finally {
      setQcSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function handleQcDecline(orderId) {
    const key = String(orderId);
    const note = window.prompt('Catatan QC reject (optional):', '');
    if (note === null) return;

    try {
      setQcSaving((prev) => ({ ...prev, [key]: true }));
      const response = await OrdersAPI.qcCheck(orderId, { decision: 'declined', note });

      if (response?.message) {
        window.alert(response.message);
      }

      await load();
    } catch (e) {
      window.alert(e.message || 'Failed to reject QC.');
    } finally {
      setQcSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    applyFilters();
  }

  function toggleSort(key) {
    setSortBy((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
    setPage(1);
  }

  const serverFilteredItems = useMemo(() => {
    if (!statusFilter) return items;
    return items.filter((row) => deriveOrderStatusView(row) === statusFilter);
  }, [items, statusFilter]);

  const sortedItems = useMemo(
    () => sortRows(serverFilteredItems, sortBy),
    [serverFilteredItems, sortBy]
  );

  const safePage = Math.min(page, Math.max(1, Number(meta?.total_pages || 1)));
  const pagedItems = sortedItems;
  const serverTotalPages = Math.max(1, Number(meta?.total_pages || 1));

  async function buildCurrentRowsForExport() {
    const detailedRows = await hydrateOrdersToItems(orders);
    const filteredRows = filterRows(detailedRows, { statusFilter, hasVoucherOnly, receivedFrom, receivedTo });

    return sortRows(filteredRows, sortBy);
  }

  async function buildAllRowsForExport() {
    const allOrders = await fetchAllOrdersForExport();
    return sortRows(mapOrderSummariesToExportRows(allOrders), sortBy);
  }

  async function exportCSV({ exportAll = false } = {}) {
    const mode = exportAll ? 'all-csv' : 'csv';

    try {
      setExportingMode(mode);
      setHydrating(true);
      setError('');

      const exportRows = exportAll ? await buildAllRowsForExport() : await buildCurrentRowsForExport();
      const matrix = buildExportRows(exportRows);
      const csvLines = [
        EXPORT_HEADERS.join(','),
        ...matrix.map((row) => row.map(escapeCsvCell).join(',')),
      ];

      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${exportAll ? 'orders_items_export_all' : 'orders_items_export'}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      anchor.click();

      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setHydrating(false);
      setExportingMode('');
    }
  }

  async function exportXLSX({ exportAll = false } = {}) {
    const mode = exportAll ? 'all-xlsx' : 'xlsx';

    try {
      setExportingMode(mode);
      setHydrating(true);
      setError('');

      const XLSX = await ensureXLSX();
      const exportRows = exportAll ? await buildAllRowsForExport() : await buildCurrentRowsForExport();
      const matrix = buildExportRows(exportRows);

      const worksheet = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...matrix]);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Items');
      XLSX.writeFile(workbook, `${exportAll ? 'orders_items_export_all' : 'orders_items_export'}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setHydrating(false);
      setExportingMode('');
    }
  }

  if (loading) {
    return <div>Loading…</div>;
  }

  return (
    <div className="bagspa-page orders-page">
      <div className="bagspa-page-header orders-page-header">
        <div>
          <h2 className="mb-0">Orders (per Item)</h2>
        </div>
      </div>

      <div className="bagspa-filter-card orders-control-card">
        <form onSubmit={handleSearchSubmit}>
          {/* TOP ROW */}
          <div className="orders-control-top-row">
            <div className="bagspa-filter-field orders-filter-search">
              <label className="form-label">Search</label>
              <div className="input-group orders-search-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by order no, SKU, customer name, notes, or voucher code…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <div className="input-group-append">
                  <button type="submit" className="btn btn-primary">
                    Search
                  </button>
                </div>
              </div>
            </div>

            <div className="bagspa-filter-field orders-filter-status">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={`${option.value || 'all'}-${option.label}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="bagspa-filter-field orders-filter-voucher">
              <label className="form-label">Voucher</label>
              <input
                className="form-control"
                placeholder="Filter by voucher code"
                value={voucherCode}
                onChange={(event) => setVoucherCode(event.target.value)}
              />
            </div>

            <div className="orders-date-group">
              <div className="bagspa-filter-field orders-filter-from">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={receivedFrom}
                  onChange={(event) => setReceivedFrom(event.target.value)}
                  aria-label="Received from"
                  title="Received from"
                />
              </div>

              <div className="bagspa-filter-field orders-filter-to">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={receivedTo}
                  onChange={(event) => setReceivedTo(event.target.value)}
                  aria-label="Received to"
                  title="Received to"
                />
              </div>
            </div>

            <div className="bagspa-filter-actions orders-filter-apply">
              <button type="button" className="btn btn-primary" onClick={applyFilters} disabled={loading || hydrating || !!exportingMode}>
                {hydrating ? 'Loading items…' : 'Apply'}
              </button>
            </div>
          </div>

          {/* BOTTOM ROW */}
          <div className="orders-control-bottom-row">
            <div className="orders-bottom-left">
              <div className="bagspa-filter-field orders-filter-options">
                <label className="form-label">Options</label>
                <div className="bagspa-check-row orders-options-row">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="vendorKaya"
                      checked={vendorKaya}
                      onChange={(event) => setVendorKaya(event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="vendorKaya">
                      Kaya
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="vendorBNK"
                      checked={vendorBNK}
                      onChange={(event) => setVendorBNK(event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="vendorBNK">
                      BNK
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="hasVoucherOnly"
                      checked={hasVoucherOnly}
                      onChange={(event) => setHasVoucherOnly(event.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="hasVoucherOnly">
                      Has voucher only
                    </label>
                  </div>
                </div>
              </div>

              <div className="bagspa-filter-field orders-filter-rows">
                <label className="form-label">Rows</label>
                <select
                  className="form-control"
                  value={per}
                  onChange={(event) => {
                    setPer(parseInt(event.target.value, 10));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((value) => (
                    <option key={value} value={value}>
                      {value} orders/page
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="orders-bottom-right">
              <button
                type="button"
                className="btn btn-outline-primary bagspa-btn-outline"
                onClick={() => exportCSV()}
                disabled={!!exportingMode || sortedItems.length === 0}
              >
                {exportingMode === 'csv' ? 'Preparing…' : 'Export CSV'}
              </button>

              <button
                type="button"
                className="btn btn-outline-primary bagspa-btn-outline"
                onClick={() => exportCSV({ exportAll: true })}
                disabled={!!exportingMode}
              >
                {exportingMode === 'all-csv' ? 'Preparing…' : 'Export All CSV'}
              </button>

              <button
                type="button"
                className="btn btn-outline-primary bagspa-btn-outline"
                onClick={() => exportXLSX()}
                disabled={!!exportingMode || sortedItems.length === 0}
              >
                {exportingMode === 'xlsx' ? 'Preparing…' : 'Export XLSX'}
              </button>

              <button
                type="button"
                className="btn btn-outline-primary bagspa-btn-outline"
                onClick={() => exportXLSX({ exportAll: true })}
                disabled={!!exportingMode}
              >
                {exportingMode === 'all-xlsx' ? 'Preparing…' : 'Export All XLSX'}
              </button>

              <Link to={`${BASE}/orders/new`} className="btn btn-primary">
                + New Order
              </Link>
            </div>
          </div>
        </form>

        <div className="text-muted small orders-showing-text">
          Showing {pagedItems.length} item rows • Orders loaded: {orders.length} of {meta.total}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div
        className="alert alert-secondary py-2"
        style={{ fontSize: 13, lineHeight: 1.5 }}
      >
        <strong>Notes:</strong><br />
        • <strong>Send to Vendor</strong> = barang sedang diproses vendor.<br />
        • <strong>Back to BNK / QC</strong> = barang sudah kembali ke store dan masuk proses QC.<br />
        • Diperlukan <strong>2 checker berbeda</strong> untuk meloloskan QC.<br />
        • <strong>QC2</strong> hanya dapat dilakukan setelah <strong>QC1</strong> selesai.<br />
        • Checkbox QC yang sudah dipakai akan otomatis terkunci untuk user yang sama.<br />
        • Setelah <strong>Bulk Tanda Keluar</strong>, order dan shipment akan masuk ke <strong>Waiting Customer Pickup</strong>.<br />
        • Order baru pindah ke <strong>Complete</strong> saat shipment benar-benar di-check <strong>Delivered</strong> oleh tim WH.<br />
        • Jika gagal QC → klik <strong>Decline QC</strong> untuk dikemas ulang dan dikembalikan ke vendor.
      </div>

      <div className="bagspa-table-card orders-table-card">
        <div className="table-responsive">
          <table className="table table-sm table-striped table-bordered align-middle orders-table">
            <thead className="thead-light">
              <tr>
                <SortableTh
                  sortBy={sortBy}
                  sortKey="order_id"
                  onToggle={toggleSort}
                  className="text-nowrap"
                  style={{ width: 80 }}
                >
                  Order ID
                </SortableTh>

                <SortableTh
                  sortBy={sortBy}
                  sortKey="order_no"
                  onToggle={toggleSort}
                  className="text-nowrap"
                >
                  Order No
                </SortableTh>

                <th>Status</th>
                <th>Vendor</th>
                <th>Item</th>

                <SortableTh sortBy={sortBy} sortKey="brand" onToggle={toggleSort}>
                  Brand
                </SortableTh>

                <SortableTh sortBy={sortBy} sortKey="category" onToggle={toggleSort}>
                  Category
                </SortableTh>

                <th>Order Type</th>
                <th>Treatment</th>

                <SortableTh sortBy={sortBy} sortKey="voucher" onToggle={toggleSort}>
                  Voucher
                </SortableTh>

                <SortableTh sortBy={sortBy} sortKey="customer" onToggle={toggleSort}>
                  Customer
                </SortableTh>

                <th>Delivery Cost</th>
                <th>Initial Price (Admin)</th>
                <th>Initial Price customer</th>
                <th>Final Price (from Kaya)</th>
                <th>Final Price customer</th>

                <SortableTh
                  sortBy={sortBy}
                  sortKey="received_at"
                  onToggle={toggleSort}
                  className="text-nowrap"
                >
                  Receive Date
                </SortableTh>

                <SortableTh
                  sortBy={sortBy}
                  sortKey="completion_date"
                  onToggle={toggleSort}
                  className="text-nowrap"
                >
                  Completion Date
                </SortableTh>

                <th>QC 1</th>
                <th>QC 2</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {pagedItems.map((row) => {
                const isSaving = !!qcSaving[String(row.order_id)];

                return (
                  <tr key={`${row.order_id}:${row.item_id ?? 'x'}`}>
                    <td className="text-monospace">{row.order_id}</td>

                    <td>
                      <Link to={`${BASE}/orders/${row.order_id}`} className="font-weight-bold">
                        {row.order_no}
                      </Link>
                    </td>

                    <td>{statusBadge(row)}</td>
                    <td>{vendorBadge(row.vendor)}</td>
                    <td className="small">{row.spec || <span className="text-muted">—</span>}</td>
                    <td>{row.brand_name || '—'}</td>
                    <td>{row.category_name || '—'}</td>
                    <td>{orderTypeLabel(row.order_type)}</td>
                    <td>{row.treatment || '—'}</td>

                    <td>
                      <VoucherCell voucher={row.voucher} />
                    </td>

                    <td>
                      {row.customer?.name || '—'}
                      <br />
                      <small className="text-muted">{row.customer?.email || ''}</small>
                    </td>

                    <td>{fmtIDR(row.delivery_cost)}</td>
                    <td>{fmtIDR(row.initial_vendor_price)}</td>
                    <td>{fmtIDR(row.initial_fixed_price)}</td>
                    <td>{fmtIDR(row.final_vendor_price)}</td>
                    <td>{fmtIDR(row.final_fixed_price)}</td>
                    <td>{fmtDate(row.received_at)}</td>
                    <td>{fmtDate(row.completion_date)}</td>

                    <td className="text-center small">
                      <QcCell
                        row={row}
                        slot={1}
                        isSaving={isSaving}
                        currentAdminId={currentAdminId}
                        onCheck={handleQcCheck}
                      />
                    </td>

                    <td className="text-center small">
                      <QcCell
                        row={row}
                        slot={2}
                        isSaving={isSaving}
                        currentAdminId={currentAdminId}
                        onCheck={handleQcCheck}
                      />
                    </td>

                    <td>
                      <div className="btn-group btn-group-sm" style={{ gap: 6, flexWrap: 'wrap' }}>
                        <Link className="btn btn-outline-primary" to={`${BASE}/orders/${row.order_id}`}>
                          Detail
                        </Link>

                        {canDeclineQc(row, isSaving, currentAdminId) && (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleQcDecline(row.order_id)}
                          >
                            Decline QC
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {pagedItems.length === 0 && (
                <tr>
                  <td colSpan={21} className="text-center text-muted">
                    {hydrating ? 'Loading items…' : 'No items found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bagspa-pagination-row orders-pagination-row">
          <div className="text-muted small">
            Page {safePage} / {serverTotalPages}
          </div>

          <div className="btn-group">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage <= 1}
              onClick={() => setPage(1)}
            >
              &laquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              &lsaquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage >= serverTotalPages}
              onClick={() => setPage((prev) => Math.min(serverTotalPages, prev + 1))}
            >
              &rsaquo;
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage >= serverTotalPages}
              onClick={() => setPage(serverTotalPages)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>

      {qcModal.open && (
        <div className="qc-modal-backdrop" role="presentation" onClick={closeQcModal}>
          <div
            className="qc-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qc-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="qc-modal-header">
              <div>
                <p className="qc-modal-eyebrow">Quality Control</p>
                <h5 id="qc-modal-title" className="qc-modal-title">
                  {qcModal.title}
                </h5>
              </div>

              <button
                type="button"
                className="qc-modal-close"
                onClick={closeQcModal}
                aria-label="Close QC modal"
              >
                ×
              </button>
            </div>

            <div className="qc-modal-body">
              <div className="qc-modal-icon">✓</div>

              <p className="qc-modal-message">{qcModal.message}</p>

              <p className="qc-modal-helper">
                {qcModal.helper}
              </p>
            </div>

            <div className="qc-modal-footer">
              <button
                type="button"
                className="btn btn-primary qc-modal-ok"
                onClick={closeQcModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
